/**
 * Yoruba catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade Standard Yoruba (with tone marks) at a mathematician's register.
 * **Warrant → «Ẹ̀rí»** (proof/warrant — flagged). Scientific loanwords are used where Yoruba
 * standardly borrows (logic → Lọ́jíìkì, modal, heuristic, geometry, postulate). W/M grades stay
 * Latin (`::numbering-system/latn`). Yoruba has NO grammatical plural — the only CLDR cardinal
 * category is `other`, so `frameActive` declares just `other` (plus an explicit `=0`). Per
 * INV-STABLE-ID a wrong translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const YO_CATALOG = {
    locale: 'yo',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Ìwádìí',
        [P2.sectionClaims]: 'Àwọn àbá',
        [P2.sectionIntegrity]: 'Ìdúróṣinṣin',
        [P2.axisWarrantLabel]: 'Ẹ̀rí',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Onírìírí',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Ìtumọ̀} interp {Ìtúmọ̀-ọ̀rọ̀} testable {Ṣeé dánwò} tested {A dánwò} refuted {A tako} program {Ìtòlẹ́sẹẹsẹ ìwádìí} philos {Onímọ̀ọ́nàmọ̀ọ́nà} other {Àìmọ̀}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ipele agbára {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Ṣeé ṣàwárí',
        [P2.stateRefuted]: 'A tako',
        [P2.stateOutside]: 'Lẹ́yìn àgbègbè',
        [P2.verdictConflict]: 'Ó takora pẹ̀lú {constraints}',
        [P2.verdictOutside]: 'Ó nílò {missing}',
        [P2.lossRegisterPlain]: 'A sọ ọ́ di rọrùn sí èdè gbáradé — apá kan pípéye ni a fi sílẹ̀.',
        [P2.lossRegisterKid]: 'A sọ ọ́ di rọrùn fún àwọn òǹkàwé kékeré — ọ̀pọ̀ àlàyé ni a fi sílẹ̀.',
        [P2.lossProfileDetail]: 'A ṣàkópọ̀ àkọsílẹ̀ kíkún ti Ẹ̀rí / Onírìírí / Modal níhìn-ín.',
        [P2.lossConflictDetail]: 'A gé àwọn àlàyé ìjà kúrú nínú ìwòye yìí.',
        [P2.laneVerified]: 'A jẹ́rìí sí',
        [P2.laneUnverified]: 'Kò tíì jẹ́rìí sí',
        [P2.laneConflict]: 'A jẹ́rìí ìjà',
        [P2.constraintLogic]: 'Lọ́jíìkì',
        [P2.constraintLightBends]: 'Ìmọ́lẹ̀ ń tẹ̀ nínú pápá òòfà',
        [P2.constraintParallelPostulate]: 'Postulate ti àwọn ìlà tó bára mu',
        [P2.claimScalarGravity]: 'Àbá òòfà scalar',
        [P2.claimNonEuclidean]: 'Geometry tí kìí ṣe ti Euclid',
        [P2.claimSubstrateSr]: 'Ìtúmọ̀ substrate ti ìbátan pàtàkì',
        [P2.edgeExtObservation]: 'Àkíyèsí òde',
        [P2.edgeResourceSample]: 'Ohun-èlò ìṣirò àjọpín',
        [P2.edgeHeuristicSample]: 'Ìtọ́sọ́nà heuristic',
        [P2.edgeSharedSample]: 'Ìpìlẹ̀ àjọpín',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Àwọn ìdènà',
        [P2.sectionEdges]: 'Àwọn ìhà',
        [P2.sectionFrame]: 'Férémù',
        [P2.sectionExposure]: 'Ìfihàn',
        [P2.statusAdoptedFormal]: 'A gbà (lọ́nà tó tọ́)',
        [P2.statusAdoptedInterpretive]: 'A gbà (lọ́nà ìtúmọ̀)',
        [P2.statusSuspended]: 'A dáwọ́ dúró',
        [P2.edgeEffectConstraint]: 'Ó ṣẹ̀dá ìdènà (ó lè tako)',
        [P2.edgeEffectResource]: 'Ohun-èlò — kò ní ipa lórí àwọn ìdájọ́',
        [P2.edgeEffectHeuristic]: 'Heuristic — kò ní ipa lórí àwọn ìdájọ́',
        [P2.edgeEffectShared]: 'Ìpìlẹ̀ àjọpín — kò ní ipa lórí àwọn ìdájọ́',
        [P2.frameActive]: '{count, plural, =0 {Kò sí ìdènà tí ń ṣiṣẹ́} other {# ìdènà tí ń ṣiṣẹ́}}',
        [P2.exposureNone]: 'Kò sí ìgbẹ́kẹ̀lé ìtúmọ̀',
        [P2.exposureDep]: 'Ó gbára lé {id}',
        [P2.laneTrackMachine]: 'Ẹ̀rọ',
        [P2.laneTrackExpert]: 'Akọ́ṣẹ́mọṣẹ́',
        [P2.laneTrackCommunity]: 'Àdúgbò',
        [P2.expertAudited]: 'A ṣàyẹ̀wò',
        [P2.expertInProgress]: 'Ń lọ lọ́wọ́',
        [P2.expertNotReviewed]: 'Kò tíì ṣàtúnyẹ̀wò',
        [P2.communityFlags]: 'Àwọn àmì wà',
        [P2.communityNone]: 'Kò sí àmì',
        [P2.controlChallenge]: 'Tako',
        [P2.controlActivate]: 'Mú ṣiṣẹ́',
        [P2.controlMakeInterpretive]: 'Sọ di ti ìtúmọ̀',
        [P2.controlImportEdge]: 'Kó ìhà wọlé',
        [P2.controlPromote]: 'Gbé ga',
        [P2.controlUndo]: 'Ṣàtúnṣe padà',
        [P2.controlRedo]: 'Ṣe lẹ́ẹ̀kansí',
        [P2.controlRegister]: 'Forúkọsílẹ̀',
        [P2.promoteRejected]: 'A kọ ìgbéga',
        [P2.promoteAccepted]: 'A gba ìgbéga',
        [P2.promoteReasonRefutationTerminal]: 'Ẹ̀rí kò lè wo àtakò sàn.',
        [P2.promoteReasonKeystone]: 'Ìgbéga pàtàkì nílò àtìlẹ́yìn onírìírí.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Irú onírìírí kò tíì wà ní ìdúró.',
        [P2.promoteReasonModalNotFixed]: 'Ipele modal kò tíì wà ní ìdúró.',
        [P2.promoteReasonWarrantNotImproved]: 'Ipele Ẹ̀rí kò ní ìlọsíwájú.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=yo.js.map